import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Select, Button, Table, Popconfirm, Space, Typography, message } from 'antd';
import { eventoService, turmaService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';

// Fase B.1: so configuracao de Turmas elegiveis para a Sessao -- NAO
// mostra alunos (calculo de elegibilidade e Fase B.2, ainda nao existe).
const TurmasElegiveisModal = ({ open, evento, sessao, podeEditar, onClose }) => {
  const [vinculos, setVinculos] = useState([]);
  const [turmasDisponiveis, setTurmasDisponiveis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [turmaSelecionada, setTurmaSelecionada] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const podeConfigurar = podeEditar && sessao?.status === 'AGENDADA';

  const carregar = useCallback(async () => {
    if (!sessao) return;
    setLoading(true);
    try {
      const [respVinculos, respTurmas] = await Promise.all([
        eventoService.listTurmasSessao(evento.id, sessao.id),
        turmaService.list({ id_unidades: evento.id_unidades }),
      ]);
      setVinculos(respVinculos.data || []);
      setTurmasDisponiveis(respTurmas.data || []);
    } catch (err) {
      handleApiError(err, 'Erro ao carregar turmas elegíveis');
    } finally {
      setLoading(false);
    }
  }, [evento, sessao]);

  useEffect(() => {
    if (open) {
      setTurmaSelecionada(null);
      carregar();
    }
  }, [open, carregar]);

  const opcoesDisponiveis = useMemo(() => {
    const idsVinculados = new Set(vinculos.map((v) => v.id_turma));
    return turmasDisponiveis
      .filter((t) => !idsVinculados.has(t.id))
      .map((t) => ({ value: t.id, label: t.nome_turm }));
  }, [turmasDisponiveis, vinculos]);

  const handleVincular = async () => {
    if (!turmaSelecionada) return;
    setSalvando(true);
    try {
      await eventoService.vincularTurmaSessao(evento.id, sessao.id, turmaSelecionada);
      message.success('Turma vinculada');
      setTurmaSelecionada(null);
      carregar();
    } catch (err) {
      handleApiError(err, 'Erro ao vincular turma');
    } finally {
      setSalvando(false);
    }
  };

  const handleDesvincular = async (vinculo) => {
    try {
      await eventoService.desvincularTurmaSessao(evento.id, sessao.id, vinculo.id);
      message.success('Turma removida');
      carregar();
    } catch (err) {
      handleApiError(err, 'Não foi possível remover a turma');
    }
  };

  const columns = [
    { title: 'Turma', dataIndex: 'turma_nome', key: 'turma_nome' },
    { title: 'Modalidade', dataIndex: 'turma_modalidade', key: 'turma_modalidade', render: (v) => v || '—' },
    { title: 'Horário', dataIndex: 'turma_horario', key: 'turma_horario', render: (v) => v || '—' },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_, vinculo) =>
        podeConfigurar && (
          <Popconfirm title="Remover esta turma da sessão?" onConfirm={() => handleDesvincular(vinculo)}>
            <Button size="small" danger type="text">
              Remover
            </Button>
          </Popconfirm>
        ),
    },
  ];

  return (
    <Modal
      title={sessao ? `Turmas elegíveis — ${sessao.nome}` : 'Turmas elegíveis'}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {!podeConfigurar && sessao?.status !== 'AGENDADA' && (
        <Typography.Paragraph type="secondary">
          Esta sessão está cancelada — não é possível alterar as turmas elegíveis.
        </Typography.Paragraph>
      )}

      {podeConfigurar && (
        <Space style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 260 }}
            placeholder="Selecione uma turma para adicionar"
            options={opcoesDisponiveis}
            value={turmaSelecionada}
            onChange={setTurmaSelecionada}
            showSearch
            optionFilterProp="label"
            notFoundContent="Nenhuma turma disponível desta unidade"
          />
          <Button type="primary" onClick={handleVincular} loading={salvando} disabled={!turmaSelecionada}>
            Adicionar
          </Button>
        </Space>
      )}

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={vinculos}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'Nenhuma turma vinculada a esta sessão' }}
      />
    </Modal>
  );
};

export default TurmasElegiveisModal;
